/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useMemo } from "react";
import { useTransition, animated } from "@react-spring/web";
import { useDevice } from "design_system/src";
import styles from "./VerticalStepScrolling.module.scss";


interface VerticalStepScrollingProps {
  name: string;
  title?: React.ReactElement;
  data: React.ReactElement[];
  sectionRef: React.RefObject<HTMLDivElement>;
}


const VerticalStepScrolling: React.FC<VerticalStepScrollingProps> = ({
  name,
  title,
  data,
  sectionRef
}) => {
  const {
    isMobile: isMobileDevice,
    isTablet: isTabletDevice,
    isDesktop: isDesktopDevice,
    isLoading
  } = useDevice();
  const [isMobile, setIsMobile] = useState<boolean>();
  const [isTablet, setIsTablet] = useState<boolean>();
  const [isDesktop, setIsDesktop] = useState<boolean>();
  const factor = useMemo(() => isMobile || isTablet ? 1.25 : 1, [isMobile, isTablet]);

  useEffect(() => {
    if (!isLoading) {
      setIsMobile(isMobileDevice);
      setIsTablet(isTabletDevice);
      setIsDesktop(isDesktopDevice && !isTabletDevice);
    }
  }, [isMobileDevice, isTabletDevice, isDesktopDevice, isLoading]);

  const containerRef = sectionRef;
  const [containerHeight, setContainerHeight] = useState<number>();
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [scrollProgress, setScrollProgress] = useState<number>(0);

  const activeIndex = useMemo(
    () => scrollProgress >= 0
      ? Math.min(
          Math.floor(scrollProgress * data.length),
          data.length - 1
        )
      : 0
  , [scrollProgress]);

  const visibleItem = useMemo(() => [data[activeIndex]], [activeIndex]);

  const isPositionFixed = useMemo(() => {
    if (isMobile) {
      return scrollProgress > 0 && scrollProgress < 1.05;
    }
    if (isTablet) {
      return scrollProgress > -0.1 && scrollProgress < 1.1;
    }
    return scrollProgress > -0.05 && scrollProgress < 1.05;
  }, [scrollProgress, isMobile]);
  const isPositionAbsolute = useMemo(() => scrollProgress >= 1.05, [scrollProgress]);

  const transitions = useTransition(visibleItem, {
    keys: activeIndex,
    from: { opacity: 0, transform: "translateY(20px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-20px)" },
    config: { tension: 280, friction: 30 },
  });

  const handleScroll = () => {
    if (!containerRef.current || !containerHeight) return;

    const containerBottom = containerRef.current!.offsetTop + window.innerHeight;
    const scrollBottom = window.scrollY + window.innerHeight;

    const rawProgress = (scrollBottom - containerBottom) / (containerHeight - window.innerHeight);
    const newProgress = Math.max(0, rawProgress);
    setScrollProgress(rawProgress);
  };

  useEffect(() => {
    if (data.length) {
      setContainerHeight(window.innerHeight * (data.length / factor));
    }
    setContainerWidth(containerRef.current?.clientWidth ?? 0);
  }, [containerRef, isMobile, data.length]);

  useEffect(() => {
    if (containerHeight !== undefined) {
      handleScroll();
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [containerHeight, isMobile]);

  if (isMobile === undefined) return <div />;

  return (
    <div
      ref={containerRef}
      className={`${styles.VerticalStepScrolling}`}
      style={{
        minHeight: `${containerHeight || window.innerHeight}px`
      }}
    >
      <div
        className={
          `display_flex flex_col ${styles.ScrollingContent} ${isPositionFixed ? styles.ScrollingContentFixed : isPositionAbsolute ? styles.ScrollingContentAbsolute : ""}`
        }
        style={{
          width: `${containerWidth}px`,
          height: `${0.9 * window.innerHeight}px`,
          justifyContent: isPositionFixed ? "center" : isPositionAbsolute ? "end" : "start"
        }}
      >
        {title && title}
        <div
          className={styles.ScrollingContainer}
          style={{
            flexGrow: isMobile ? 1 : isTablet ? 0.3 : 0.5,
            alignItems: "center",
          }}
        >
          <div className={styles.ScrollingIndicatorContainer}>
            {data.map((_, index) => {
              const handleIndicatorClick = () => {
                if (!containerRef.current || !containerHeight) return;

                const stepHeight = (containerHeight - window.innerHeight) / (data.length - 1);
                const targetScroll = containerRef.current.offsetTop + (stepHeight * index);
                window.scrollTo({ top: targetScroll, behavior: "smooth" });
              };
              return(
                <div
                  key={`${name}-indicator-${index}`}
                  className={`${styles.ScrollingIndicator}${index === activeIndex ? ` ${styles["ScrollingIndicator--active"]}` : ""}`}
                  onClick={handleIndicatorClick}
                />
              );
            })}
          </div>
          <div
            className={styles.ScrollingCardsContainer}
            style={{ flexGrow: 1, }}
          >
            {transitions((style, item) => (
              <animated.div
                key={`${name}-${activeIndex}`}
                style={style}
                className={styles.ScrollingCard}
              >
                {item}
              </animated.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerticalStepScrolling;
